import {
  CreateParams,
  DeleteParams,
  GetListParams,
  GetOneParams,
  GetManyParams,
  UpdateParams,
  DeleteManyParams,
  fetchUtils,
} from 'react-admin'

import { getExistingRoles } from '@/services/api/methods/getExistingRoles'
import { getAllFees } from '@/services/api/methods/getAllFees'
import { getAllSettlement } from '@/services/api/methods/getAllSettlement'

import { EResource } from './types'
import simpleRestProvider from 'ra-data-simple-rest'

import {
  getCustomList,
  getCustomOne,
  customCreate,
  customDelete,
  customUpdate,
  getCustomMany,
  adminsUpdate,
  paramsMapper,
} from '@/services/api/helpers'
import { API_BASE_URL, CUSTOM_JSON_HEADERS, LIST_REQUEST_TIMEOUT_MS } from '@/services/api/consts'
import { axiosDZAuthInstance } from '@/services/api/axios'
import { AxiosRequestConfig, AxiosError } from 'axios'
import { getBulkPromoCodeCampaigns, getBulkPromoCodeCampaign } from '@/services/api/methods/apiBulkPromoCodes'
import { getPromoRules, getPromoRule, updatePromoRule } from '@/services/api/methods/apiPromoRules'
import {
  createSupplierMapping,
  deleteSupplierMapping,
  getMapping,
  parseCompositeId,
  updateMapping,
} from '@/services/api/methods/apiSupplierMappings'
import { getSupplierByCode } from '@/services/api/methods/apiSuppliers'
import { getVariantCost, getVariantCostsList } from '@/services/api/methods/apiVariantCosts'
import { getCommunityCampaign, getCommunityCampaigns } from '@/services/api/methods/apiCommunityCampaigns'
// Custom fetchJson that uses our axios instance with interceptors
const fetchJson = async (url: string, options: fetchUtils.Options = {}) => {
  const { method = 'GET', body } = options

  try {
    const config: AxiosRequestConfig = {
      method: method as 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH',
      url,
      headers: CUSTOM_JSON_HEADERS,
    }

    if (body) {
      config.data = body
    }

    const response = await axiosDZAuthInstance(config)

    return {
      status: response.status,
      statusText: response.statusText,
      headers: new Headers(),
      body: response.data,
      json: response.data,
    }
  } catch (error) {
    const axiosError = error as AxiosError
    if (axiosError.response) {
      throw new Error(`HTTP ${axiosError.response.status}: ${axiosError.response.statusText}`)
    }
    throw error
  }
}

const baseDataProvider = simpleRestProvider(`${API_BASE_URL}/dashboard`, fetchJson)

export const dataProvider = {
  ...baseDataProvider,
  getList: async (resource: string, params: GetListParams) => {
    switch (resource) {
      case EResource.Orders:
        // Fail fast and do not retry: a slow orders list must not pile up more heavy queries on the database
        return getCustomList(resource, params, { timeout: LIST_REQUEST_TIMEOUT_MS, 'axios-retry': { retries: 0 } })
      case EResource.CommunityCampaigns:
        return getCommunityCampaigns()
      case EResource.Roles:
        return getExistingRoles()
      case EResource.Fees:
        return getAllFees(params)
      case EResource.Settlement:
        return getAllSettlement(params)
      case EResource.BulkPromoCodes:
        // getBulkPromoCodeCampaigns already returns { data, total } format
        return getBulkPromoCodeCampaigns(params)
      case EResource.PromoRules:
        // getPromoRules already returns { data, total } format
        return getPromoRules(paramsMapper(params))
      case EResource.VariantCosts:
        return getVariantCostsList(params)
      case EResource.SupplierMappings:
        return getCustomList(resource, params).then(({ data, total }) => ({
          data: data.map((record: { variantId: number; supplierCode: string }) => ({
            ...record,
            id: `${record.variantId}-${record.supplierCode}`,
          })),
          total,
        }))
      case EResource.Suppliers:
        return getCustomList(resource, params).then(({ data, total }) => ({
          data: data.map((record: { code: string }) => ({
            ...record,
            id: record.code,
          })),
          total,
        }))
      default:
        return getCustomList(resource, params)
    }
  },
  getMany: (resource: string, params: GetManyParams) => {
    switch (resource) {
      default:
        return getCustomMany(resource, params)
    }
  },
  getOne: (resource: string, params: GetOneParams) => {
    switch (resource) {
      case EResource.CommunityCampaigns:
        return getCommunityCampaign(params.id)
      case EResource.BulkPromoCodes:
        return getBulkPromoCodeCampaign(params.id as number).then((data) => ({ data: { ...data, id: data.id } }))
      case EResource.PromoRules:
        return getPromoRule(params.id as string).then((data) => ({ data: { ...data, id: data.id } }))
      case EResource.SupplierMappings: {
        const [variantId, supplierCode] = parseCompositeId(String(params.id))
        return getMapping(variantId, supplierCode).then((data) => ({
          data: { ...data, id: params.id },
        }))
      }
      case EResource.Suppliers:
        return getSupplierByCode(String(params.id)).then((data) => ({
          data: { ...data, id: data.code },
        }))
      case EResource.VariantCosts:
        return getVariantCost(params.id as number | string).then((data) => ({ data }))
      default:
        return getCustomOne(resource, params)
    }
  },
  create: (resource: string, params: CreateParams) => {
    switch (resource) {
      case EResource.BulkPromoCodes:
        // Bulk promo codes creation is handled directly in the form component
        // This should not be called, but we'll handle it gracefully
        return Promise.resolve({ data: { id: 0, ...params.data } })
      case EResource.SupplierMappings:
        return createSupplierMapping(params)
      default:
        return customCreate(resource, params)
    }
  },
  update: (resource: string, params: UpdateParams) => {
    switch (resource) {
      case EResource.Admins:
        return adminsUpdate(resource, params)
      case EResource.PromoRules:
        return updatePromoRule(params.id as string, params.data).then((data) => ({ data: { ...data, id: data.id } }))
      case EResource.SupplierMappings:
        return updateMapping(params)
      default:
        return customUpdate(resource, params)
    }
  },
  delete: (resource: string, params: DeleteParams) => {
    switch (resource) {
      case EResource.BulkPromoCodes:
        // Bulk promo codes deletion is handled directly in the component
        // This should not be called, but we'll handle it gracefully
        return Promise.resolve({ data: { id: params.id } })
      case EResource.SupplierMappings:
        return deleteSupplierMapping(params)
      default:
        return customDelete(resource, params)
    }
  },
  deleteMany: (resource: string, params: DeleteManyParams) => {
    switch (resource) {
      case EResource.SupplierMappings:
        return Promise.all(params.ids.map((id) => deleteSupplierMapping({ id }))).then((responses) => ({
          data: responses.map(({ data }) => data.id),
        }))
      default:
        return Promise.all(params.ids.map((id) => customDelete(resource, { id }))).then((responses) => ({
          data: responses.map(({ data }) => data.id),
        }))
    }
  },
}
